import { useCallback, useEffect, useMemo, useState } from "react";

import { type DocumentData, type ModelFields, type QueryModel, type WithModel } from "@doitintl/models-any";
import {
  type TransformMethod,
  type TransformMethodReturnType,
  type TReturnData,
  type TypeFromTransform,
  useCollectionDataOnce,
} from "@doitintl/models-hooks";
import get from "lodash/get";

type TResult<
  TModel extends DocumentData,
  IDField extends TypeFromTransform<TTransformModel>,
  RefField extends TypeFromTransform<TTransformModel>,
  TTransformModel,
> = {
  handlePrevPage: (() => void) | undefined;
  handleNextPage: (() => void) | undefined;
  items: TReturnData<TModel, IDField, RefField, TTransformModel>[] | undefined;
  isLoading: boolean;
  hasNextPage: boolean | undefined;
  hasPrevPage: boolean | undefined;
};

export type PaginatedQueryProps<
  TModel extends DocumentData,
  IDField extends string | undefined,
  RefField extends string | undefined,
  TTransformModel,
> = {
  query: QueryModel<TModel> | undefined;
  options: {
    orderBy: Array<string & keyof ModelFields<TModel>>;
    rowsPerPage?: number;
    orderDirection?: "asc" | "desc";
    transform?: TransformMethod<TModel, TTransformModel>;
    idField?: IDField;
    refField?: RefField;
  };
};

export const useSortHandler = <TModel extends DocumentData>(
  defaultOrderBy: Array<string & keyof ModelFields<TModel>>,
  defaultOrderDirection?: "asc" | "desc" | undefined
) => {
  const [orderDirection, setOrderDirection] = useState<"asc" | "desc">(defaultOrderDirection ?? "asc");
  const [orderBy, setOrderBy] = useState<Array<string & keyof ModelFields<TModel>>>(defaultOrderBy);

  const createSortHandler = useCallback(
    (properties: Array<string & keyof ModelFields<TModel>>) => () => {
      const isAsc = orderBy[0] === properties[0] && orderDirection === "asc";
      setOrderBy(properties);
      setOrderDirection(isAsc ? "desc" : "asc");
    },
    [orderBy, orderDirection]
  );

  return { orderBy, orderDirection, createSortHandler };
};

export const usePaginatedQuery = <
  TModel extends DocumentData,
  IDField extends TypeFromTransform<TTransformModel>,
  RefField extends TypeFromTransform<TTransformModel>,
  TTransformModel,
>({
  query,
  options: { orderBy, rowsPerPage, orderDirection, transform, idField, refField },
}: PaginatedQueryProps<TModel, IDField, RefField, TTransformModel>): TResult<
  TModel,
  IDField,
  RefField,
  TTransformModel
> => {
  const [hasStartPrev, setHasStartPrev] = useState<{ hasPrev?: boolean; hasNext?: boolean }>({});
  const [displayItems, setDisplayItems] = useState<TReturnData<TModel, IDField, RefField, TTransformModel>[]>();
  const [nonTransformItems, setNonTransformItems] = useState<Array<WithModel<TModel>>>([]);

  const actualRowsPerPage = rowsPerPage ?? 10;

  const transformWrapper: TransformMethod<TModel, TTransformModel> = useCallback(
    (val, snapshot) => {
      setNonTransformItems((prev) => [...(prev ?? []), val]);

      if (transform) {
        return transform(val, snapshot);
      }
      return val as TransformMethodReturnType<TModel, TTransformModel>;
    },
    [transform]
  );

  const [startAtOrEndAt, setStartAtOrEndAt] = useState<{
    values?: unknown[];
    direction?: "prev" | "next";
  }>({});

  const prepareQuery = useMemo(() => {
    if (!query) {
      return undefined;
    }

    let q = query;
    orderBy.forEach((field, i) => {
      q = q.orderBy(field, i === 0 ? orderDirection : "asc");
    });

    const isDirectionNext = !startAtOrEndAt.direction || startAtOrEndAt.direction === "next";

    if (isDirectionNext) {
      q = q.limit(actualRowsPerPage + 1);
      if (startAtOrEndAt.values) {
        q = q.startAfter(...startAtOrEndAt.values);
      }
    } else {
      q = q.limitToLast(actualRowsPerPage + 1);
      if (startAtOrEndAt.values) {
        q = q.endBefore(...startAtOrEndAt.values);
      }
    }

    return q;
  }, [actualRowsPerPage, orderBy, orderDirection, query, startAtOrEndAt.direction, startAtOrEndAt.values]);

  const [newItems, isLoading] = useCollectionDataOnce(prepareQuery, { transform: transformWrapper, idField, refField });

  useEffect(() => {
    setHasStartPrev({});
    setDisplayItems(undefined);
    setStartAtOrEndAt({});
    setNonTransformItems([]);
  }, [query]);

  useEffect(() => {
    if (!newItems) {
      return;
    }

    setHasStartPrev((prevHasStartPrev) => {
      const newHasStartPrev = { ...prevHasStartPrev };

      const isDirectionNext = !startAtOrEndAt.direction || startAtOrEndAt.direction === "next";

      if (isDirectionNext) {
        const newDisplayItems = newItems.slice(0, actualRowsPerPage);

        setDisplayItems(newDisplayItems);

        newHasStartPrev.hasNext = newItems.length > actualRowsPerPage;
        newHasStartPrev.hasPrev = !!startAtOrEndAt.direction;
      } else {
        const hasExtraItem = newItems.length > actualRowsPerPage;
        const displayItems = hasExtraItem
          ? newItems.slice(1) // skip the first because it was used for paging
          : newItems;
        setDisplayItems(displayItems);

        newHasStartPrev.hasPrev = newItems.length > actualRowsPerPage;
        newHasStartPrev.hasNext = true;
      }

      return newHasStartPrev;
    });
  }, [actualRowsPerPage, newItems, rowsPerPage, startAtOrEndAt.direction, transform]);

  const handlePageChange = useCallback(
    (direction: "prev" | "next", item: WithModel<TModel>) => {
      setNonTransformItems([]);
      setStartAtOrEndAt(() => {
        const values = orderBy.map((orderByField) => get(item, orderByField));

        return { direction, values };
      });
    },
    [orderBy]
  );

  const handlePrevPage = useCallback(() => {
    if (!nonTransformItems || nonTransformItems.length === 0) {
      return;
    }

    const itemIndex = nonTransformItems.length === actualRowsPerPage + 1 ? 1 : 0;
    const item = nonTransformItems[itemIndex];
    handlePageChange("prev", item);
  }, [nonTransformItems, actualRowsPerPage, handlePageChange]);

  const handleNextPage = useCallback(() => {
    if (!nonTransformItems || nonTransformItems.length === 0) {
      return;
    }

    const item = nonTransformItems[Math.min(actualRowsPerPage - 1, nonTransformItems.length - 1)];
    handlePageChange("next", item);
  }, [nonTransformItems, actualRowsPerPage, handlePageChange]);

  return {
    handlePrevPage: hasStartPrev.hasPrev ? handlePrevPage : undefined,
    handleNextPage: hasStartPrev.hasNext ? handleNextPage : undefined,
    items: displayItems,
    isLoading,
    hasNextPage: hasStartPrev.hasNext,
    hasPrevPage: hasStartPrev.hasPrev,
  };
};
